<template>
  <v-container fluid class="forms-container pa-0">
    <!-- Empty State -->
    <v-card
      v-if="forms.length <= 0"
      fluid
      class="whiteBackGround customBorderRadius"
    >
      <first-page
        :img-src="imgSrc"
        :dialog-name.sync="noDialog"
        :points="points"
        :headline="headline"
        :btn-url="btnUrl"
      ></first-page>
    </v-card>

    <!-- Main Content -->
    <div v-if="forms.length > 0">
      <!-- Modern Header -->
      <div
        v-if="showHeader"
        class="header-section mb-6"
        style="margin: 0 12px; position: relative;"
      >
        <v-btn icon small class="header-close-btn" @click="dismissHeader">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <div class="d-flex align-center mb-4">
          <div class="header-icon-wrapper mr-4">
            <v-icon size="32" color="#FFFFFF"
              >mdi-file-document-multiple</v-icon
            >
          </div>
          <div>
            <h1 class="page-title mb-1">Forms</h1>
            <p class="page-subtitle mb-0">Manage and create custom forms</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="white"
            class="primary--text"
            large
            depressed
            style="border-radius: 8px;"
            @click="$router.push({ name: 'New Form' })"
          >
            <v-icon left>mdi-plus</v-icon>
            Create Form
          </v-btn>
        </div>

        <!-- Feature Pills -->
        <v-row class="feature-pills ma-0">
          <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
            <div class="feature-pill">
              <v-icon size="18" color="white" class="mr-2"
                >mdi-form-select</v-icon
              >
              <span>Form Builder</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
            <div class="feature-pill">
              <v-icon size="18" color="white" class="mr-2"
                >mdi-chart-box</v-icon
              >
              <span>Data Collection</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
            <div class="feature-pill">
              <v-icon size="18" color="white" class="mr-2"
                >mdi-file-chart</v-icon
              >
              <span>Reports</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
            <div class="feature-pill">
              <v-icon size="18" color="white" class="mr-2">mdi-download</v-icon>
              <span>Export Data</span>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Forms Grid -->
      <v-row class="ma-0 px-3">
        <!-- Loader -->
        <template v-if="loader">
          <v-col
            v-for="n in 5"
            :key="n"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
            class="pa-2"
          >
            <v-skeleton-loader type="card, actions" />
          </v-col>
        </template>

        <!-- Forms List -->
        <template v-else>
          <!-- Create New Form Card -->
          <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pa-2">
            <v-card
              class="modern-card customBorderRadius text-center d-flex flex-column"
              elevation="0"
              height="280"
              style="cursor: pointer; border: 2px dashed #37cf37;"
              @click="$router.push({ name: 'New Form' })"
            >
              <v-card-text
                class="d-flex flex-column align-center justify-center flex-grow-1"
              >
                <div class="icon-badge mb-4" style="width: 80px; height: 80px;">
                  <v-icon size="40" color="#37cf37">mdi-plus</v-icon>
                </div>
                <h3 class="primary--text mb-2">Create New Form</h3>
                <p class="grey--text text--darken-1 mb-0">
                  Build custom forms in minutes
                </p>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Existing Form Cards -->
          <v-col
            v-for="(form, i) in forms"
            :key="i"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            xl="2"
            class="pa-2"
          >
            <v-card
              class="modern-card customBorderRadius d-flex flex-column"
              elevation="0"
              height="280"
            >
              <!-- Card Header with Delete -->
              <div class="card-header d-flex align-center">
                <div class="icon-badge mr-3" style="width: 40px; height: 40px;">
                  <v-icon color="#FFFFFF" size="20">mdi-file-document</v-icon>
                </div>
                <div class="flex-grow-1 mr-2">
                  <h4
                    class="card-title mb-0 text-truncate"
                    style="font-size: 14px;"
                  >
                    {{ form.name }}
                  </h4>
                </div>
                <v-btn icon x-small @click.stop="removeForm(form, i)">
                  <v-icon size="18" color="white">mdi-close</v-icon>
                </v-btn>
              </div>

              <v-divider></v-divider>

              <!-- Card Content -->
              <v-card-text class="flex-grow-1 pa-4">
                <p
                  class="grey--text text--darken-1 mb-0"
                  style="font-size: 13px; line-height: 1.5;"
                >
                  {{ form.desc || "No description" }}
                </p>
              </v-card-text>

              <!-- Card Actions -->
              <v-divider></v-divider>
              <v-card-actions class="pa-3">
                <v-btn
                  color="primary"
                  outlined
                  small
                  style="border-radius: 6px;"
                  @click="
                    $router.push({ name: 'Edit Form', params: { id: form.id } })
                  "
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  outlined
                  small
                  style="border-radius: 6px;"
                  @click="
                    $router.push({
                      name: 'Edit Form',
                      params: { id: form.id, tab: 3 },
                    })
                  "
                >
                  <v-icon left small>mdi-eye</v-icon>
                  View
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import firstPage from "@/components/firstPage.vue";
export default {
  components: {
    firstPage,
  },
  data() {
    return {
      showHeader: false,
      noDialog: false,
      btnUrl: "/newform",
      headline: "Supercharge Your Data Collection",
      points: [
        "Build forms in minutes: Multiple choice, short answer, checkboxes - design it all with ease.",
        "Empower telecallers: Focus on conversations, not note-taking. Collect data seamlessly during calls.",
        "Actionable insights: Download reports, analyze trends, make data-driven decisions. ",
      ],
      forms: [], // collection of forms will be stored here
      loader: true, // to wait till the data is ready
    };
  },
  computed: {
    imgSrc() {
      return require("../assets/gotask.svg");
    },
  },
  created() {
    this.showHeader = localStorage.getItem("formHeaderDismissed") !== "true";
    // getting all the forms in this account at the time of creation of the page
    this.$http
      .get(
        `${this.$store.state.ENDPOINT}/forms?filter[where][companyId]=${this.$store.state.companyDetails.id}`,
      )
      .then((res) => {
        this.forms = res.data;
        this.loader = false;
      })
      .catch((err) => {
        console.log(err);
      });
  },

  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("formHeaderDismissed", "true");
    },
    // removing the form
    async removeForm(form, index) {
      var flag = 0; // to store if the user confirms to delete the form
      // await as the next step depends to the user's choice
      await this.$swal({
        type: "warning",
        text: "Are you sure you want to delete?",
        showCancelButton: true,
        confirmButtonColor: "#31a848",
        cancelButtonColor: "#F44336",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((yes) => {
        // user confirmation
        if (yes.value) {
          flag = 1;
        }
      });

      if (flag == 1) {
        // removing the forms from database
        this.$http
          .delete(`${this.$store.state.ENDPOINT}/forms/${form.id}`)
          .then((res) => {
            this.$swal({
              type: "success",
              text: "Form is successfully deleted",
            });
          })
          .catch((err) => {
            console.log(err);
          });
        // removing the form from the array so that no refresh is needed
        this.forms.splice(index, 1);
      }
    },
  },
};
</script>

<style scoped>
.forms-container {
  background-color: var(--v-background-base, #f5f5f5);
}

.theme--dark .forms-container {
  background-color: #121212 !important;
}

.modern-card {
  background: var(--v-background-base, white);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.modern-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.card-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 12px 16px;
  color: white;
}

.card-title {
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.icon-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
</style>
